import React from "react";
import _ from "lodash";
import { FormattedMessage } from "react-intl";

import { useForm } from "@sportal/cdk/form/Form";
import {
  DeviceNameInput,
  MacAddressInput,
  ProfileSelect,
} from "../../../../components/devices/commonComponents";
import {
  CommonModalFooter,
  CommonModalHeader,
  ModalDialog,
  ModalService,
} from "../../../../components/modal";
import {
  getLogicalDevicesEntities,
  saveDevice,
} from "../../../../store/devices/logicalDevices";
import { useIntlTranslate } from "../../../../hooks";
import { getMappedProfilesForSelect } from "../../../../store/profiles/profiles.selectors";
import { SBLogicalDevice } from "../../../../store/devices/devices.types";
import { ProfileForSelect } from "../../../../store/profiles/profiles.types";

const CLASS_NAME = "add-device-modal--by-mac";

interface OpenDeviceCreationByMacDialogProps {
  currentProfile: string;
}
interface AddDeviceByMacDialogProps {
  modal: any;
  currentProfile: string;
  allDevices: Record<string, SBLogicalDevice>;
  profiles: ProfileForSelect[];
}

export const openDeviceCreationByMacDialog =
  ({ currentProfile }: OpenDeviceCreationByMacDialogProps) =>
  (dispatch, getState) => {
    const state = getState();
    const allDevices = getLogicalDevicesEntities(state);
    const profiles = getMappedProfilesForSelect(state);

    ModalService.show((modal) => ({
      dialog: (
        <AddDeviceByMacDialog
          modal={modal}
          currentProfile={currentProfile}
          allDevices={allDevices}
          profiles={profiles}
        />
      ),
    }))
      .then((device) => dispatch(saveDevice(device)))
      .catch(_.noop);
  };

const AddDeviceByMacDialog = ({
  modal,
  currentProfile,
  allDevices,
  profiles,
}: AddDeviceByMacDialogProps) => {
  const translate = useIntlTranslate();
  const formProps = useForm({ address: "", name: "", profile: currentProfile });

  const close = () => modal.reject();
  const submit = () =>
    modal.resolve({
      identifiers: [formProps.values.address],
      name: formProps.values.name,
      profile: formProps.values.profile,
    });

  const handleAddressChange = (address: string, error: string) => {
    formProps.set("address", address);
    formProps.setError("address", error);
  };
  const handleDeviceNameChange = (name: string, error: string) => {
    formProps.set("name", name);
    formProps.setError("name", error);
  };
  const handleProfileChange = (profile: string) => {
    formProps.set("profile", profile);
  };

  const isSubmitDisabled =
    !formProps.didFormChange() ||
    formProps.hasErrors() ||
    !formProps.values.name ||
    !formProps.values.address;

  return (
    <ModalDialog
      className={CLASS_NAME}
      rejectHandler={close}
      submitHandler={submit}
      disabled={isSubmitDisabled}
    >
      <CommonModalHeader>
        <FormattedMessage id={"add_device_by_mac"} />
      </CommonModalHeader>
      <ModalDialog.Body>
        <MacAddressInput
          address={formProps.values.address}
          label={translate("mac_address")}
          devices={allDevices}
          handleChange={handleAddressChange}
          error={
            formProps.errors.address && translate(formProps.errors.address)
          }
          autoFocus
        />
        <DeviceNameInput
          handleChange={handleDeviceNameChange}
          label={translate("device_name")}
          devices={allDevices}
          name={formProps.values.name}
          error={formProps.errors.name && translate(formProps.errors.name)}
        />
        <ProfileSelect
          profiles={profiles}
          profile={formProps.values.profile}
          handleChange={handleProfileChange}
          className={CLASS_NAME}
        />
      </ModalDialog.Body>
      <CommonModalFooter submitMessage={translate("add_device")} />
    </ModalDialog>
  );
};
